import {
  Card,
  CardHeader,
  CardBody,
  Text,
  Flex,
  Box,
  Grid,
  ButtonLink,
  Skeleton,
} from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { useQltySummary } from '../../../quality';
import {
  RiFlaskFill,
  RiErrorWarningLine,
  RiForbid2Line,
  RiCodeLine,
  RiBrainLine,
  RiArrowRightUpLine,
} from '@remixicon/react';
import { EmptyState } from '../../../../shared/components';
import styles from './quality-summary-card.module.css';

function scoreColor(score: number): string {
  if (score > 80) return '#10d48e';
  if (score >= 50) return '#f59e0b';
  return '#ff3d5a';
}

const Header = () => {
  const { t } = useTranslationRef(appTranslationRef);
  return (
    <Flex gap="3" align="center">
      <RiFlaskFill size={18} className={styles.headerIcon} />
      <Text variant="title-small" weight="bold">
        {t('entity.tabs.quality')}
      </Text>
    </Flex>
  );
};

export const QualitySummaryCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const entityRef = stringifyEntityRef(entity);
  const { loading, error, data: summary } = useQltySummary(entityRef);

  const namespace = (entity.metadata.namespace ?? 'default').toLocaleLowerCase('en-US');
  const kind = entity.kind.toLocaleLowerCase('en-US');
  const name = entity.metadata.name.toLocaleLowerCase('en-US');
  const qualityUrl = `/quality/${namespace}/${kind}/${name}`;

  if (loading) {
    return (
      <Card>
        <CardHeader><Header /></CardHeader>
        <CardBody>
          <Flex direction="column" gap="4">
            <Skeleton style={{ height: 48, borderRadius: 8 }} />
            <Flex gap="3">
              <Skeleton style={{ flex: 1, height: 56, borderRadius: 8 }} />
              <Skeleton style={{ flex: 1, height: 56, borderRadius: 8 }} />
            </Flex>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader><Header /></CardHeader>
        <CardBody>
          <Text style={{ color: 'var(--bui-fg-danger)' }}>{error}</Text>
        </CardBody>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card>
        <CardHeader><Header /></CardHeader>
        <CardBody>
          <EmptyState title={t('quality.summary.noData')} />
        </CardBody>
      </Card>
    );
  }

  const color = scoreColor(summary.healthScore);
  const gatesPassed = summary.gates.filter(g => g.status === 'ok').length;
  const gatesTotal = summary.gates.length;

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center">
          <Header />
          <Box className={styles.scoreBadge} style={{ color, borderColor: `${color}40` }}>
            {Math.round(summary.healthScore)}
            <span className={styles.scoreUnit}>/100</span>
          </Box>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="5">
          {/* Score bar */}
          <Flex direction="column" gap="2">
            <Box className={styles.scoreBar}>
              <Box
                className={styles.scoreFill}
                style={{ width: `${summary.healthScore}%`, backgroundColor: color }}
              />
            </Box>
            <Flex justify="between" align="center">
              <Text variant="body-x-small" color="secondary">
                {(() => {
                  if (summary.healthScore > 80) return t('quality.summary.scoreGood');
                  if (summary.healthScore >= 50) return t('quality.summary.scoreFair');
                  return t('quality.summary.scorePoor');
                })()}
              </Text>
              {gatesTotal > 0 && (
                <Text variant="body-x-small" color="secondary">
                  {gatesPassed}/{gatesTotal} {t('quality.summary.gatesPassed')}
                </Text>
              )}
            </Flex>
          </Flex>

          {/* KPI Grid */}
          <Grid.Root columns="2" gap="3">
            <Grid.Item colSpan="1">
              <Flex align="center" gap="3" px="2" py="2" className={styles.kpi}>
                <Flex style={{ flexShrink: 0 }} className={styles.kpiIcon}>
                  <RiErrorWarningLine size={16} />
                </Flex>
                <Text variant="body-medium" weight="bold">{summary.totalIssues}</Text>
                <Text variant="body-x-small" color="secondary">{t('quality.summary.issues')}</Text>
              </Flex>
            </Grid.Item>
            <Grid.Item colSpan="1">
              <Flex align="center" gap="3" px="2" py="2" className={styles.kpi}>
                <Flex
                  className={styles.kpiIcon}
                  style={summary.blockingIssues > 0 ? { color: '#ff3d5a', flexShrink: 0 } : { flexShrink: 0 }}
                >
                  <RiForbid2Line size={16} />
                </Flex>
                <Text
                  variant="body-medium"
                  weight="bold"
                  style={summary.blockingIssues > 0 ? { color: '#ff3d5a' } : undefined}
                >
                  {summary.blockingIssues}
                </Text>
                <Text variant="body-x-small" color="secondary">{t('quality.summary.blocking')}</Text>
              </Flex>
            </Grid.Item>
            <Grid.Item colSpan="1">
              <Flex align="center" gap="3" px="2" py="2" className={styles.kpi}>
                <Flex style={{ flexShrink: 0 }} className={styles.kpiIcon}>
                  <RiCodeLine size={16} />
                </Flex>
                <Text variant="body-medium" weight="bold">
                  {summary.totalLoc.toLocaleString()}
                </Text>
                <Text variant="body-x-small" color="secondary">{t('quality.heatmap.loc')}</Text>
              </Flex>
            </Grid.Item>
            <Grid.Item colSpan="1">
              <Flex align="center" gap="3" px="2" py="2" className={styles.kpi}>
                <Flex style={{ flexShrink: 0 }} className={styles.kpiIcon}>
                  <RiBrainLine size={16} />
                </Flex>
                <Text variant="body-medium" weight="bold">
                  {summary.cognitiveAvgPerFn}
                </Text>
                <Text variant="body-x-small" color="secondary">{t('quality.summary.cognitiveAvg')}</Text>
              </Flex>
            </Grid.Item>
          </Grid.Root>

          {/* Action */}
          <ButtonLink
            href={qualityUrl}
            variant="secondary"
            size="small"
            iconStart={<RiArrowRightUpLine size={16} />}
          >
            {t('quality.summary.openQualityCenter')}
          </ButtonLink>
        </Flex>
      </CardBody>
    </Card>
  );
};
